import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { USER_TYPE_OPTIONS, UserType } from './userTypes';
import './platform-stats-user-lookup.scss';
import './platform-stats-user-types.scss';

interface LookupUser {
  id: string;
  name: string | null;
  companyName: string | null;
}

interface UserRef {
  id: string;
  name: string | null;
}

interface OnboardingBlocker {
  id: string;
  kind: string;
  category: string | null;
  provider: string | null;
  detail: string;
  consentToRecontact: boolean;
  resolvedAt: string | null;
  createdAt: string;
}

interface ReengagementInfo {
  state: string;
  stoppedReason: string | null;
  resumeAt: string | null;
  lastUserMessageAt: string | null;
  stuckFlaggedAt: string | null;
  nudges: {
    inviteNudgeSentAt: string | null;
    inviteDeferralNudgeSentAt: string | null;
    connectNudgeSentAt: string | null;
    connectFinalNudgeSentAt: string | null;
  };
  blockers: OnboardingBlocker[];
}

interface UserDetail {
  userId: string;
  name: string | null;
  signedUpAt: string | null;
  userType: UserType;
  onboarding: {
    hasOnboardingSession: boolean;
    isLegacyUser: boolean;
    onboardingCompleted: boolean;
    onboardingCompletedAt: string | null;
    assistantName: string | null;
    referredBy: UserRef | null;
    referralCodeUsed: string | null;
    referralSource: string | null;
    emailConnected: boolean;
    connectedEmails: string[];
    kits: Array<{ slug: string; label: string }>;
    callScheduled: boolean;
    callScheduledFor: string | null;
    callConfirmed: boolean;
    callConfirmedAt: string | null;
    callConfirmedBy: UserRef | null;
  };
  reengagement: ReengagementInfo | null;
  subscription: {
    status: string;
    planName: string | null;
    planDescription: string | null;
  } | null;
  subscriptionLookupFailed: boolean;
  referrals: {
    total: number;
    withAccounts: number;
    withActiveSubscriptions: number;
  };
}

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const NUDGE_LABELS: Array<{
  key: keyof ReengagementInfo['nudges'];
  label: string;
}> = [
  { key: 'inviteNudgeSentAt', label: 'Invite nudge' },
  { key: 'inviteDeferralNudgeSentAt', label: 'Invite deferral nudge' },
  { key: 'connectNudgeSentAt', label: 'Connect reminder' },
  { key: 'connectFinalNudgeSentAt', label: 'Final connect reminder' },
];

const REENGAGEMENT_STATE_LABELS: Record<string, string> = {
  active: 'Active (eligible for nudges)',
  deferred: 'Deferred',
  stopped: 'Stopped (no further messages)',
};

const YesNo: FC<{ value: boolean; yes?: string; no?: string }> = ({
  value,
  yes = 'Yes',
  no = 'No',
}) => (
  <span className={`yes-no ${value ? 'yes' : 'no'}`}>{value ? yes : no}</span>
);

/**
 * Admin "User Lookup" tab: pick a user and see their onboarding status
 * (signup, referral, email connection, kits, onboarding call, subscription,
 * referrals made) plus an action to confirm the onboarding call happened.
 */
const PlatformStatsUserLookup: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [users, setUsers] = useState<LookupUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [savingType, setSavingType] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{ success: boolean; users: LookupUser[] }>(
          '/platform-stats/user-detail/users',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) setUsers(res.data.users);
      } catch {
        setError('Failed to load user list');
      }
    })();
  }, [getAccessTokenSilently]);

  const fetchDetail = useCallback(
    async (uid: string) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{ success: boolean; detail: UserDetail }>(
          `/platform-stats/user-detail?userId=${encodeURIComponent(uid)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) setDetail(res.data.detail);
      } catch {
        setError('Failed to load user detail');
      } finally {
        setLoading(false);
      }
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    if (selectedUserId) {
      setDetail(null);
      fetchDetail(selectedUserId);
    }
  }, [selectedUserId, fetchDetail]);

  const confirmCall = async () => {
    if (!selectedUserId || confirming) return;
    if (
      !window.confirm(
        'Confirm that this user has had their onboarding call? This is recorded as the authoritative signal that the call happened.',
      )
    ) {
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/platform-stats/user-detail/confirm-onboarding-call',
        { userId: selectedUserId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchDetail(selectedUserId);
    } catch {
      setError('Failed to confirm the onboarding call');
    } finally {
      setConfirming(false);
    }
  };

  const changeUserType = async (userType: UserType) => {
    if (!detail || savingType || detail.userType === userType) return;
    const previous = detail.userType;
    setDetail({ ...detail, userType });
    setSavingType(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/platform-stats/user-types/users/${encodeURIComponent(detail.userId)}`,
        { userType },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      setDetail((d) => (d ? { ...d, userType: previous } : d));
      setError('Failed to save user type — change reverted');
    } finally {
      setSavingType(false);
    }
  };

  const ob = detail?.onboarding;
  const re = detail?.reengagement;
  const nudgesSent = re ? NUDGE_LABELS.filter(({ key }) => re.nudges[key]) : [];

  return (
    <div className='user-lookup'>
      <div className='lookup-controls'>
        <select
          className='user-filter-select'
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
        >
          <option value=''>Select a user…</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.id}
              {u.companyName ? ` (${u.companyName})` : ''}
            </option>
          ))}
        </select>
      </div>

      {error && <div className='msg error'>{error}</div>}
      {loading && <div className='loading'>Loading…</div>}

      {!loading && !detail && !error && (
        <div className='lookup-empty'>
          Select a user to see their onboarding status.
        </div>
      )}

      {!loading && detail && ob && (
        <div className='lookup-detail'>
          <div className='detail-card'>
            <h3>{detail.name || detail.userId}</h3>
            {ob.isLegacyUser && (
              <div className='no-session-note'>
                Legacy user — signed up before WhatsApp onboarding, so they are
                treated as fully onboarded; dates below fall back to the account
                record.
              </div>
            )}
            <dl>
              <dt>First signed up</dt>
              <dd>{formatDateTime(detail.signedUpAt)}</dd>

              <dt>User type</dt>
              <dd>
                <select
                  className={`user-type-select type-${detail.userType}`}
                  value={detail.userType}
                  disabled={savingType}
                  aria-label='User type'
                  onChange={(e) => changeUserType(e.target.value as UserType)}
                >
                  {USER_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {savingType && <span className='detail-sub'>Saving…</span>}
              </dd>

              <dt>Referred by</dt>
              <dd>
                {ob.referredBy
                  ? ob.referredBy.name || ob.referredBy.id
                  : 'No referrer'}
                {ob.referralCodeUsed ? ` (code ${ob.referralCodeUsed})` : ''}
              </dd>

              <dt>Email connected</dt>
              <dd>
                <YesNo value={ob.emailConnected} />
                {ob.connectedEmails.length > 0 && (
                  <span className='detail-sub'>
                    {' '}
                    {ob.connectedEmails.join(', ')}
                  </span>
                )}
              </dd>

              <dt>Kits</dt>
              <dd>
                {ob.kits.length > 0 ? (
                  ob.kits.map((k) => (
                    <span key={k.slug} className='kit-chip'>
                      {k.label}
                    </span>
                  ))
                ) : (
                  <span>None</span>
                )}
              </dd>

              <dt>Onboarding completed</dt>
              <dd>
                <YesNo value={ob.onboardingCompleted} />
                {ob.onboardingCompletedAt && (
                  <span className='detail-sub'>
                    {' '}
                    {formatDateTime(ob.onboardingCompletedAt)}
                  </span>
                )}
              </dd>

              <dt>Onboarding call scheduled</dt>
              <dd>
                {ob.callScheduled ? (
                  <>
                    <YesNo value />
                    {ob.callScheduledFor && (
                      <span className='detail-sub'>
                        {' '}
                        for {formatDateTime(ob.callScheduledFor)}
                      </span>
                    )}
                  </>
                ) : (
                  <YesNo value={false} yes='Yes' no='Not scheduled' />
                )}
              </dd>

              <dt>Onboarding call happened</dt>
              <dd>
                {ob.callConfirmed ? (
                  <>
                    <YesNo value yes='Confirmed' />
                    <span className='detail-sub'>
                      {' '}
                      {ob.isLegacyUser
                        ? '(legacy user — assumed to have happened)'
                        : `by ${
                            ob.callConfirmedBy?.name ||
                            ob.callConfirmedBy?.id ||
                            'admin'
                          } on ${formatDateTime(ob.callConfirmedAt)}`}
                    </span>
                  </>
                ) : (
                  <>
                    <YesNo value={false} no='Not confirmed' />
                    {ob.hasOnboardingSession && (
                      <button
                        className='confirm-call-btn'
                        onClick={confirmCall}
                        disabled={confirming}
                      >
                        {confirming ? 'Confirming…' : 'Confirm call happened'}
                      </button>
                    )}
                  </>
                )}
              </dd>

              <dt>Active subscription</dt>
              <dd>
                {detail.subscriptionLookupFailed ? (
                  <span className='detail-sub'>Lookup failed</span>
                ) : detail.subscription ? (
                  <>
                    <YesNo value />
                    <span className='detail-sub'>
                      {' '}
                      {detail.subscription.planName || 'Unknown plan'} (
                      {detail.subscription.status})
                    </span>
                  </>
                ) : (
                  <YesNo value={false} no='No active subscription' />
                )}
              </dd>

              {re && !ob.onboardingCompleted && (
                <>
                  <dt>Re-engagement</dt>
                  <dd>
                    {REENGAGEMENT_STATE_LABELS[re.state] || re.state}
                    {re.state === 'stopped' && re.stoppedReason && (
                      <span className='detail-sub'> — {re.stoppedReason}</span>
                    )}
                    {re.state === 'deferred' && re.resumeAt && (
                      <span className='detail-sub'>
                        {' '}
                        — resumes {formatDateTime(re.resumeAt)}
                      </span>
                    )}
                  </dd>

                  <dt>Nudges sent</dt>
                  <dd>
                    {nudgesSent.length > 0 ? (
                      nudgesSent.map(({ key, label }) => (
                        <div key={key}>
                          {label}
                          <span className='detail-sub'>
                            {' '}
                            {formatDateTime(re.nudges[key])}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span>None</span>
                    )}
                  </dd>

                  {re.stuckFlaggedAt && (
                    <>
                      <dt>Flagged “I’m stuck”</dt>
                      <dd>
                        <YesNo value yes='Yes' />
                        <span className='detail-sub'>
                          {' '}
                          {formatDateTime(re.stuckFlaggedAt)}
                        </span>
                      </dd>
                    </>
                  )}
                </>
              )}

              {re && re.blockers.length > 0 && (
                <>
                  <dt>Onboarding blockers</dt>
                  <dd>
                    {re.blockers.map((b) => (
                      <div key={b.id} className='blocker-row'>
                        <span className='blocker-kind'>
                          {b.kind}
                          {b.category ? ` / ${b.category}` : ''}
                          {b.provider ? ` (${b.provider})` : ''}
                        </span>
                        <span className='detail-sub'> {b.detail}</span>
                        <span className='detail-sub'>
                          {' '}
                          — {formatDateTime(b.createdAt)},{' '}
                          {b.resolvedAt
                            ? `resolved ${formatDateTime(b.resolvedAt)}`
                            : 'unresolved'}
                          , recontact consent:{' '}
                          {b.consentToRecontact ? 'yes' : 'no'}
                        </span>
                      </div>
                    ))}
                  </dd>
                </>
              )}

              <dt>Referrals made</dt>
              <dd>
                {detail.referrals.total}
                <span className='detail-sub'>
                  {' '}
                  ({detail.referrals.withAccounts} with accounts,{' '}
                  {detail.referrals.withActiveSubscriptions} with active
                  subscriptions)
                </span>
              </dd>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformStatsUserLookup;
